import { useCallback, useState } from "react";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import {
  Flex,
  View,
  DialogContainer,
  Dialog,
  Content,
  Header,
  Heading,
} from "@adobe/react-spectrum";
import { ChevronDown, ChevronUp } from "react-bootstrap-icons";
import { FilterIcon } from "../../../../components/protected/Icons/IconsLib";
import { useSelector } from "react-redux";
import { RootState } from "../../../../state/store";
import { useTranslation } from "react-i18next";
import styles from "./filter_component.module.css";
import FolderTypeFilterComponent from "../../../../components/protected/layout-components/FilterComponent/FolderTypeFilter/FolderTypeFilterComponent";
import IndustryFilterComponent from "../../../../components/protected/layout-components/FilterComponent/IndustryFilter/IndustryFilterComponent";
import DistributionGroupFilterComponent from "../../../../components/protected/layout-components/FilterComponent/DistributionGroupFilter/DistributionGroupFilterComponent";
import { useDeviceDetection } from "../../../../hooks/UseDeviceDetection";

interface IFilterComponentProps {
  onFilterChange: (filterParams: {}) => void;
  filterParams: any;
  filterStyle?: any;
}

const FilterComponent: React.FC<IFilterComponentProps> = ({
  onFilterChange,
  filterParams,
  filterStyle,
}) => {
  const selectedApp = useSelector(
    (state: RootState) => state.userWebLayout.selectedAppId
  );
  const { t } = useTranslation('contacts');
  const _t = (key: string) => t(`contact_filters.${key}`, { ns: 'contacts' });
  const { isMobile } = useViewInfo();
  const [show, setShow] = useState(false);
  const [selectedView, setSelectedView] = useState<string>("");
  const [contactType, setContacttype] = useState<string>("");
  const [selectedFolder, setSelectedFolder] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [clearFilters, setClearFilters] = useState<boolean>(false);
  const [toggleListVisibility, setToggleListVisibility] = useState<any>({
    stateList: true,
  });
  const deviceInfo = useDeviceDetection(isMobile);

  const createParams = useCallback(
    async (filterTypeName: any, filterValue: any) => {
      //initializeFilterParams();
      const filterValueStr =
        filterTypeName === "contactType"
          ? filterValue
          : Array.from(filterValue).join(",");
      if (
        (filterTypeName == "contactType" && filterValueStr == "All Contacts") ||
        (filterTypeName == "folderType" && filterValueStr == "All folders") ||
        (filterTypeName == "industry" && filterValueStr == "All industries") ||
        (filterTypeName == "groups" && filterValueStr == "All groups")
      ) {
        filterParams.FILTER.COL = [];
        onFilterChange(filterParams);
        return;
      }
      handleFilterType(filterTypeName, filterValueStr, filterValue);
    },
    [selectedApp]
  );

  const updateFilterParams = (name: string, value: string) => {
    let updatedArray = filterParams.FILTER.COL.map((obj: any) =>
      obj.name === name ? { ...obj, value } : obj
    );
    const hasNameSearch = filterParams.FILTER.COL.some(
      (obj: any) => obj.name === name
    );

    if (!hasNameSearch) {
      updatedArray.push({ name, value });
    }
    filterParams.FILTER.COL = updatedArray;
  };

  const handleFilterType = (
    filterTypeName: any,
    filterValueStr: any,
    filterValue: any
  ) => {
    switch (filterTypeName) {
      case "contactType":
        updateFilterParams("contactType", filterValueStr);
        break;
      case "folderType":
        updateFilterParams("folderType", filterValueStr);
        break;
      case "industry":
        updateFilterParams("industry", filterValueStr);
        break;
      case "groups":
        updateFilterParams("groups", filterValueStr);
        break;
      default:
        break;
    }
    //console.log('filterParams', filterParams);
    filterParams.FILTER.COL = filterParams.FILTER.COL.filter(
      (col: any) => col.value !== ""
    );
    onFilterChange(filterParams);
  };

  const handleClearFilters = () => {
    setClearFilters(true);
    setTimeout(() => setClearFilters(false), 0);
  };

  const shouldUseMobileLayout = deviceInfo.shouldUseMobileLayout();
  const isMobileOrSmallScreen =
    shouldUseMobileLayout || deviceInfo.isIPad || deviceInfo.isTablet;

  return isMobileOrSmallScreen ? (
    <View
      position={"relative"}
      width={"100%"}
      height={"64px"}
      UNSAFE_className={styles.mobile_header_parent}
    >
      <Flex
        direction={"column"}
        position={"absolute"}
        top={"calc(50% - 16px)"}
        left={"20px"}
        align-items={"start"}
        justifyContent={"start"}
      >
        <View
          position={"relative"}
          UNSAFE_className={styles.mobile_header_text_1}
        >
          <Flex alignItems="start">
            <Heading level={3}>{_t('header_text')}</Heading>
          </Flex>
        </View>
        <View
          position={"relative"}
          UNSAFE_className={styles.mobile_header_text_2}
        >
          {/* {t('service_activities', { ns: 'layout_components' })} */}
        </View>
      </Flex>
      <View
        position={"absolute"}
        top={"calc(50% - 12px)"}
        left={"calc(100% - 40px)"}
        width={"24px"}
        height={"24px"}
        overflow={"hidden"}
      >
        <div onClick={() => setIsOpen(true)}>
          <FilterIcon size={20} />
        </div>

        <DialogContainer
          isDismissable
          onDismiss={() => {
            setIsOpen(false);
          }}
        >
          {isOpen && (
            <Dialog size="L">
              <Content>
                <Header>
                  <Flex
                    position={"relative"}
                    direction={"row"}
                    justifyContent={"center"}
                    alignContent={"center"}
                    width={"100%"}
                  >
                    <View
                      position={"relative"}
                      UNSAFE_className={styles.popup_header_text}
                    >
                      {t("filters", { ns: "layout_components" })}
                    </View>
                  </Flex>
                </Header>
                <View width={"100%"} position={"relative"} height={"100vh"}>
                  <Flex
                    direction={"column"}
                    position={"absolute"}
                    width={"95%"}
                    top={"30px"}
                    left={"16px"}
                    alignItems={"start"}
                    justifyContent={"start"}
                    gap={"24px"}
                    UNSAFE_className={styles.popup_header_body_parent}
                  >
                    <Flex
                      direction={"column"}
                      width={"100%"}
                      alignItems={"start"}
                      justifyContent={"start"}
                      gap={"12px"}
                      UNSAFE_className={styles.popup_header_body}
                    >
                      <Flex
                        direction={"row"}
                        height={"36px"}
                        alignSelf={"stretch"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        UNSAFE_className={styles.contact_type_heading}
                      >
                        <Flex
                          direction={"row"}
                          flex={1}
                          alignItems={"center"}
                          justifyContent={"start"}
                        >
                          <View
                            position={"relative"}
                            UNSAFE_className={styles.contact_type_heading_text}
                          >
                            {/* {t('folder_type', { ns: 'layout_components' })} */}
                            {_t('type')}
                          </View>
                        </Flex>

                        {toggleListVisibility.stateList ? (
                          <ChevronDown
                            color="#B0BEC5"
                            size={16}
                            onClick={() =>
                              setToggleListVisibility((preValue: any) => ({
                                ...preValue,
                                stateList: false,
                              }))
                            }
                            style={{ cursor: "pointer" }}
                          />
                        ) : (
                          <ChevronUp
                            color="#B0BEC5"
                            size={16}
                            onClick={() =>
                              setToggleListVisibility((preValue: any) => ({
                                ...preValue,
                                stateList: true,
                              }))
                            }
                            style={{ cursor: "pointer" }}
                          />
                        )}
                      </Flex>
                      <Flex
                        direction={"column"}
                        alignSelf={"stretch"}
                        alignItems={"start"}
                        justifyContent={"start"}
                        gap={"4px"}
                        UNSAFE_className={styles.filter_views_parent}
                        UNSAFE_style={{
                          display: toggleListVisibility.stateList ? "" : "none",
                        }}
                      >
                        <a
                          onClick={() => {
                            setContacttype("All Contacts");
                            handleClearFilters();
                            createParams("contactType", "All Contacts");
                          }}
                        >
                          <Flex
                            direction={"row"}
                            height={"36px"}
                            alignItems={"center"}
                            justifyContent={"center"}
                            UNSAFE_className={
                              contactType === "All Contacts"
                                ? styles.filter_views_container_selected
                                : styles.filter_views_container
                            }
                          >
                            <Flex
                              direction={"row"}
                              flex={1}
                              alignItems={"center"}
                              justifyContent={"start"}
                            >
                              <Flex
                                position={"relative"}
                                flex={1}
                                UNSAFE_className={styles.filter_views_text}
                              >
                                {_t("all_contacts")}
                              </Flex>
                            </Flex>
                          </Flex>
                        </a>
                        <a
                          onClick={() => {
                            setContacttype("1");
                            createParams("contactType", "1");
                          }}
                        >
                          <Flex
                            UNSAFE_className={
                              contactType === "1"
                                ? styles.filter_views_container_selected
                                : styles.filter_views_container
                            }
                          >
                            <Flex
                              direction={"row"}
                              flex={1}
                              alignItems={"center"}
                              justifyContent={"start"}
                            >
                              <Flex
                                position={"relative"}
                                flex={1}
                                UNSAFE_className={styles.filter_views_text}
                              >
                                {_t("persons")}
                              </Flex>
                            </Flex>
                          </Flex>
                        </a>

                        <a
                          onClick={() => {
                            setContacttype("2");
                            createParams("contactType", "2");
                          }}
                        >
                          <Flex
                            UNSAFE_className={
                              contactType === "2"
                                ? styles.filter_views_container_selected
                                : styles.filter_views_container
                            }
                          >
                            <Flex
                              direction={"row"}
                              flex={1}
                              alignItems={"center"}
                              justifyContent={"start"}
                            >
                              <Flex
                                position={"relative"}
                                flex={1}
                                UNSAFE_className={styles.filter_views_text}
                              >
                                {_t("companies")}
                              </Flex>
                            </Flex>
                          </Flex>
                        </a>
                        <a
                          onClick={() => {
                            setContacttype("3");
                            createParams("contactType", "3");
                          }}
                        >
                          <Flex
                            UNSAFE_className={
                              contactType === "3"
                                ? styles.filter_views_container_selected
                                : styles.filter_views_container
                            }
                          >
                            <Flex
                              direction={"row"}
                              flex={1}
                              alignItems={"center"}
                              justifyContent={"start"}
                            >
                              <Flex
                                position={"relative"}
                                flex={1}
                                UNSAFE_className={styles.filter_views_text}
                              >
                                {_t("system_users")}
                              </Flex>
                            </Flex>
                          </Flex>
                        </a>
                      </Flex>
                      <FolderTypeFilterComponent
                        createParams={createParams}
                        clearFilters={clearFilters}
                      />
                      <DistributionGroupFilterComponent
                        createParams={createParams}
                        clearFilters={clearFilters}
                      />
                      <IndustryFilterComponent
                        createParams={createParams}
                        clearFilters={clearFilters}
                      />
                    </Flex>
                  </Flex>
                </View>
              </Content>
            </Dialog>
          )}
        </DialogContainer>
      </View>
    </View>
  ) : (
    <View
      width={"100%"}
      position={"relative"}
      backgroundColor={"static-white"}
      UNSAFE_className={styles.filter_menu_parent}
      UNSAFE_style={filterStyle}
    >
      <Flex
        direction={"column"}
        top={"24px"}
        left={"0px"}
        width={"240px"}
        alignItems={"start"}
        justifyContent={"start"}
        gap={"5px"}
        UNSAFE_className={styles.views_menu_container}
      >
        <Flex
          direction={"row"}
          UNSAFE_className={styles.views_menu_heading}
          alignSelf={"stretch"}
          alignItems={"center"}
          justifyContent={"start"}
        >
          <View
            position={"relative"}
            UNSAFE_className={styles.menu_heading_text}
          >
            {/* {t('views', { ns: 'layout_components' })}  */}
            {_t('title')}
          </View>
        </Flex>
        <Flex
          direction={"row"}
          height={"36px"}
          alignSelf={"stretch"}
          alignItems={"center"}
          justifyContent={"space-between"}
          UNSAFE_className={styles.contact_type_heading}
        >
          <Flex
            direction={"row"}
            flex={1}
            alignItems={"center"}
            justifyContent={"start"}
          >
            <View
              position={"relative"}
              UNSAFE_className={styles.contact_type_heading_text}
            >
              {/* {t('folder_type', { ns: 'layout_components' })} */}
              {_t('type')}
            </View>
          </Flex>

          {toggleListVisibility.stateList ? (
            <ChevronDown
              color="#B0BEC5"
              size={16}
              onClick={() =>
                setToggleListVisibility((preValue: any) => ({
                  ...preValue,
                  stateList: false,
                }))
              }
              style={{ cursor: "pointer" }}
            />
          ) : (
            <ChevronUp
              color="#B0BEC5"
              size={16}
              onClick={() =>
                setToggleListVisibility((preValue: any) => ({
                  ...preValue,
                  stateList: true,
                }))
              }
              style={{ cursor: "pointer" }}
            />
          )}
        </Flex>
        <Flex
          direction={"column"}
          alignSelf={"stretch"}
          alignItems={"start"}
          justifyContent={"start"}
          gap={"4px"}
          UNSAFE_className={styles.filter_views_parent}
          UNSAFE_style={{
            display: toggleListVisibility.stateList ? "" : "none",
          }}
        >
          <a
            onClick={() => {
              setContacttype("All Contacts");
              handleClearFilters();
              createParams("contactType", "All Contacts");
            }}
          >
            <Flex
              direction={"row"}
              height={"36px"}
              alignItems={"center"}
              justifyContent={"center"}
              UNSAFE_className={
                contactType === "All Contacts"
                  ? styles.filter_views_container_selected
                  : styles.filter_views_container
              }
            >
              <Flex
                direction={"row"}
                flex={1}
                alignItems={"center"}
                justifyContent={"start"}
              >
                <Flex
                  position={"relative"}
                  flex={1}
                  UNSAFE_className={styles.filter_views_text}
                >
                  {_t("all_contacts")}
                </Flex>
              </Flex>
            </Flex>
          </a>
          <a
            onClick={() => {
              setContacttype("1");
              createParams("contactType", "1");
            }}
          >
            <Flex
              UNSAFE_className={
                contactType === "1"
                  ? styles.filter_views_container_selected
                  : styles.filter_views_container
              }
            >
              <Flex
                direction={"row"}
                flex={1}
                alignItems={"center"}
                justifyContent={"start"}
              >
                <Flex
                  position={"relative"}
                  flex={1}
                  UNSAFE_className={styles.filter_views_text}
                >
                 {_t("persons")}
                </Flex>
              </Flex>
            </Flex>
          </a>

          <a
            onClick={() => {
              setContacttype("2");
              createParams("contactType", "2");
            }}
          >
            <Flex
              UNSAFE_className={
                contactType === "2"
                  ? styles.filter_views_container_selected
                  : styles.filter_views_container
              }
            >
              <Flex
                direction={"row"}
                flex={1}
                alignItems={"center"}
                justifyContent={"start"}
              >
                <Flex
                  position={"relative"}
                  flex={1}
                  UNSAFE_className={styles.filter_views_text}
                >
                 {_t("companies")}
                </Flex>
              </Flex>
            </Flex>
          </a>
          <a
            onClick={() => {
              setContacttype("3");
              createParams("contactType", "3");
            }}
          >
            <Flex
              UNSAFE_className={
                contactType === "3"
                  ? styles.filter_views_container_selected
                  : styles.filter_views_container
              }
            >
              <Flex
                direction={"row"}
                flex={1}
                alignItems={"center"}
                justifyContent={"start"}
              >
                <Flex
                  position={"relative"}
                  flex={1}
                  UNSAFE_className={styles.filter_views_text}
                >
                 {_t("system_users")}
                </Flex>
              </Flex>
            </Flex>
          </a>
        </Flex>
      </Flex>
      <Flex
        direction={"column"}
        position={"relative"}
        left={"0px"}
        width={"240px"}
        alignItems={"start"}
        justifyContent={"start"}
        gap={"5px"}
        UNSAFE_className={styles.filters_menu_container}
      >
        <FolderTypeFilterComponent
          createParams={createParams}
          clearFilters={clearFilters}
        />
      </Flex>
      <Flex
        direction={"column"}
        position={"relative"}
        // top={toggleListVisibility.stateList ? '350px':'95px'}
        left={"0px"}
        width={"240px"}
        alignItems={"start"}
        justifyContent={"start"}
        gap={"5px"}
        UNSAFE_className={styles.filters_menu_container}
      >
        <DistributionGroupFilterComponent
          createParams={createParams}
          clearFilters={clearFilters}
        />
      </Flex>
      <Flex
        direction={"column"}
        position={"relative"}
        // top={toggleListVisibility.stateList ? '350px':'95px'}
        left={"0px"}
        width={"240px"}
        alignItems={"start"}
        justifyContent={"start"}
        gap={"5px"}
        UNSAFE_className={styles.filters_menu_container}
      >
        <IndustryFilterComponent
          createParams={createParams}
          clearFilters={clearFilters}
        />
      </Flex>
    </View>
  );
};

export default FilterComponent;
